import { Track, VideoQuality } from 'livekit-client';
import type { ConferenceMediaTier } from '../../../config/mediaConfig';
import { shouldUseLowCameraQuality } from '../../../config/mediaConfig';

/** SC-375: a gallery with this many camera tiles or fewer shows them large enough to need more than LOW. */
export const GALLERY_MEDIUM_QUALITY_MAX_TILES = 4;

/**
 * The most a camera may stream at (a cap: adaptive stream still picks the layer that fits the tile).
 *
 * Large talks cap cameras at the low simulcast layer to save bandwidth. In the gallery a handful of
 * tiles can each be half the screen, where LOW is visibly blurry, so a small gallery lifts the cap to
 * MEDIUM. Normal-sized talks get HIGH, i.e. no cap, which also lifts one left over from when the talk
 * was larger. `galleryTileCount` is null when the gallery is not shown.
 */
export function conferenceCameraQuality(
  tier: ConferenceMediaTier,
  galleryTileCount: number | null = null
): VideoQuality {
  if (!shouldUseLowCameraQuality(tier)) return VideoQuality.HIGH;
  if (galleryTileCount != null && galleryTileCount <= GALLERY_MEDIUM_QUALITY_MAX_TILES) {
    return VideoQuality.MEDIUM;
  }
  return VideoQuality.LOW;
}

/** Sets the camera cap from `conferenceCameraQuality` after subscribing to (or re-processing) a remote camera. */
export function applyConferenceCameraQuality(
  publication: { source: Track.Source; setVideoQuality?: (q: VideoQuality) => void },
  tier: ConferenceMediaTier,
  galleryTileCount: number | null = null
): void {
  if (publication.source !== Track.Source.Camera) return;
  const quality = conferenceCameraQuality(tier, galleryTileCount);
  try {
    publication.setVideoQuality?.(quality);
  } catch {
    // Publication may not support quality selection yet.
  }
}
